import React, { useEffect, useState } from 'react';
import { RolesUser, SortCondition } from 'shared/enums';
import { dataForOvertimeTable } from 'shared/helpers/utils';
import { usePullSort } from 'shared/hooks';
import { Badge, notification, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { OVERTIMES_BADGES, overtimeStatusFromBack } from 'shared/enums/overtime';
import CommonTable from 'features/tables/commonTable';
import { TCommonColumns } from 'shared/types/overtime.types';
import { useLazyGetOvertimeQuery, useOvertimeTakeToWorkMutation } from 'app/services/edpApiV1/overtime';
import SearchComponent from 'shared/components/search/rvd-overtime';
import { ISearchData } from 'shared/types/rvd.types';
import { useAppSelector } from 'app/store';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';
import { THr } from 'shared/types/types';
import { viewerModel } from 'entities/viewer';
import ModalChangeResponsible from './modalChangeResponsible';

const OvertimePull = () => {
  const [search, setSearch] = useState<ISearchData>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [sortInfo, trigger] = usePullSort();
  const [getOvertimes, { data: overtimes, isFetching, isLoading }] = useLazyGetOvertimeQuery();
  const [takeToWorkQuery, { isLoading: isTakeToWorkLoading }] = useOvertimeTakeToWorkMutation();
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const [currentTakeToWorkOvertime, setCurrentTakeToWorkOvertime] = useState<number[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });
  const accountInfo = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const navigate = useNavigate();

  useEffect(() => {
    getOvertimes({
      page: currentPage,
      size: currentSize,
      orderDate: sortInfo.value ? sortInfo.value : undefined,
      type: 'new',
      allAvailableOrganizations,
    });
  }, [getOvertimes, currentPage, currentSize, sortInfo.value]);

  useEffect(() => {
    const newData = {};
    Object.keys(search).forEach(key => {
      if (search[key]) {
        newData[key] = search[key];
      }
    });
    getOvertimes({ page: currentPage, size: currentSize, type: 'new', allAvailableOrganizations, ...newData });
  }, [currentPage, currentSize, getOvertimes, search, allAvailableOrganizations]);

  const onSearch = (data: ISearchData) => {
    setSearch({ ...data });
  };

  const onclickToRow = (id: number) => {
    navigate(`/overtime/archive/${id}`);
  };

  const takeToWork = async (overtimeId: number) => {
    setCurrentTakeToWorkOvertime([...currentTakeToWorkOvertime, overtimeId]);

    await takeToWorkQuery({ overtimeId })
      .unwrap()
      .then(() => {
        notification.success({ message: 'Взято в работу!' });
      })
      .catch((e: Error) => {
        if (!e.message) {
          notification.success({ message: 'Взято в работу!' });
        } else {
          notification.error({ message: e.message });
        }
      });
    setCurrentTakeToWorkOvertime(currentTakeToWorkOvertime.filter(elem => elem === overtimeId));
  };

  const handleChangeResponsible = (event: React.MouseEvent, id: number): void => {
    event.stopPropagation();
    setRequestId(id);
    setOpenModal(true);
  };

  const columns: TCommonColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: true,
      onHeaderCell: _ => ({
        onClick: () => trigger(),
      }),
    },
    {
      title: 'Количество сотрудников',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <span>
          <Badge status={OVERTIMES_BADGES[status]} />
          {overtimeStatusFromBack[status]}
        </span>
      ),
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {currentTakeToWorkOvertime.includes(Number(record.id)) && isTakeToWorkLoading ? (
            <Spin indicator={<LoadingOutlined spin />} />
          ) : (
            <button
              className={'loadButton'}
              onClick={e => {
                e.stopPropagation();
                takeToWork(record.id || 0);
              }}
            >
              <a>Взять в работу</a>
            </button>
          )}
        </>
      ),
    },

    {
      title: 'Комментарий',
      dataIndex: 'userComment',
      key: 'userComment',
      render: (userComment: string) => {
        return (
          <div title={userComment} className="rvd-user-comment">
            {userComment}
          </div>
        );
      },
    },
  ];

  if (!allAvailableOrganizations && accountInfo.role === RolesUser.HRDirector) {
    columns.push({
      title: 'Ответственный',
      dataIndex: 'hr',
      key: 'hr',
      render: (hr: THr, record: { key: React.Key }) => {
        return (
          <>
            {hr && (hr.name || hr.middleName || hr.lastName) ? (
              <>
                <span>{`${hr.lastName} ${hr.name} ${hr.middleName ?? ''}`}</span>
                <EditOutlined onClick={(e: React.MouseEvent) => handleChangeResponsible(e, Number(record.key))} />
              </>
            ) : (
              <button
                className={'loadButton'}
                onClick={(e: React.MouseEvent) => handleChangeResponsible(e, Number(record.key))}
              >
                <a>Назначить</a>
              </button>
            )}
          </>
        );
      },
    });
  }

  return (
    <>
      <SearchComponent onSearch={onSearch} />
      {allSubordinates && allSubordinates.length > 0 && requestId && (
        <ModalChangeResponsible
          allSubordinates={allSubordinates}
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
        />
      )}
      <Spin spinning={isLoading || isFetching}>
        <CommonTable
          dataSource={dataForOvertimeTable(overtimes?.data || [])}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={overtimes?.pagination.total}
          setCurrentSize={setCurrentSize}
          currentSize={currentSize}
          onclickToRow={(id: number) => onclickToRow(id)}
        />
      </Spin>
    </>
  );
};

export default OvertimePull;
